import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config } from 'sanitize-text/sanitizers/SanitizeConfiguration';
// @ts-expect-error untyped dependency
import FormFieldInput from './FormFieldInputContainer';
import * as DependentFieldUtils from 'FormsUI/utils/DependentFields';
import { getDependentFieldOperatorLabel } from 'FormsUI/constants/DependentFields';
import { getFormFieldError } from 'FormsUI/selectors/FormField';
import useFieldInRouting from 'FormsUI/components/hooks/routing/useFieldInRouting';
import { RemoveIcon } from 'foundations-assets/icons/components/RemoveIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RuleWrapper = styled.div.withConfig({
  displayName: "DependentFormFieldInput__RuleWrapper"
})(["position:relative;text-align:center;z-index:10;"]);
const RuleTag = styled.div.withConfig({
  displayName: "DependentFormFieldInput__RuleTag"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["background:", ";padding:0 ", " 0 ", ";border:", " solid ", ";display:inline-block;max-width:80%;text-decoration:underline;font-weight:", ";"], theme.color['fill-primary-disabled'], theme.space['100'], theme.space['250'], theme.space['25'], theme.color['fill-tertiary-pressed'], theme.typography['body-400'].fontWeight);
});
const DraggableFieldContainer = styled.div.withConfig({
  displayName: "DependentFormFieldInput__DraggableFieldContainer"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return css(["margin-top:-", ";position:relative;"], theme.space['500']);
});
const DraggableField = styled.div.withConfig({
  displayName: "DependentFormFieldInput__DraggableField"
})(({
  theme: _theme,
  $isHovered,
  $isHidden,
  $isSelected
}) => {
  const theme = _theme;
  return css(["position:relative;border:", " ", " ", ";margin-left:", ";padding:", ";cursor:pointer;opacity:", ";transition:border-color 0.15s ease-in-out;background:transparent;", " &:hover{border-color:", ";}&:focus-within{border-color:", ";outline:none;box-shadow:", ";}"], theme.space['25'], $isHovered ? 'solid' : 'dashed', theme.color['border-core-default'], $isHidden ? 0 : theme.space['400'], theme.space['500'], $isHidden ? 0.5 : 1, $isSelected && css(["border-color:", ";box-shadow:", ";"], theme.color['border-interactive-default'], theme.boxShadow['100']), theme.color['border-interactive-default'], theme.color['border-interactive-default'], theme.boxShadow['100']);
});
const DependentFormFieldInput = ({
  dependentFieldFilter,
  dependentFieldFilterIndex,
  formField,
  handleInputSelect,
  selectedFieldName,
  removeDependentField,
  isFormLabelPropertyEnabled
}) => {
  const dependentFormField = dependentFieldFilter.dependentFormField;
  const {
    name,
    objectTypeId
  } = dependentFormField;
  const {
    isUsedInRouting,
    removeRoutingFieldHandler
  } = useFieldInRouting({
    name,
    objectTypeId
  });
  const formFieldError = useSelector(state => getFormFieldError(state, dependentFormField));
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleFilterSelect = () => {
    handleInputSelect(true, formField, {
      scrollToSmartOptions: true
    });
  };
  const handleFilterRemove = () => {
    removeDependentField(formField, dependentFieldFilterIndex);
    removeRoutingFieldHandler();
  };
  const handleSelectedChange = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    handleInputSelect(true, dependentFieldFilter.dependentFormField);
  }, [handleInputSelect, dependentFieldFilter]);
  const renderDependentFieldFilterRule = () => {
    const {
      filters
    } = dependentFieldFilter;
    const {
      operator
    } = filters[0];
    const previewValue = DependentFieldUtils.getPreviewValue(formField, filters[0]);
    return /*#__PURE__*/_jsx(RuleWrapper, {
      children: /*#__PURE__*/_jsxs(RuleTag, {
        children: [/*#__PURE__*/_jsx(Link, {
          use: "unstyled",
          "data-test-id": "rule-tag-link",
          underline: true,
          onClick: handleFilterSelect,
          children: /*#__PURE__*/_jsx(Text, {
            as: "small",
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: 'editor.form.config.dependentFields.rule',
              options: {
                formFieldLabel: sanitize(formField.label, config.TEXTONLY),
                operatorLabel: getDependentFieldOperatorLabel(operator),
                previewValue
              }
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isUsedInRouting,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.form.formFields.usedInRedirectToASchedulingPage"
          }),
          children: /*#__PURE__*/_jsx(Link, {
            use: "unstyled",
            disabled: isUsedInRouting,
            onClick: handleFilterRemove,
            "data-test-id": "dependent-field-remove-button",
            children: /*#__PURE__*/_jsx(RemoveIcon, {
              size: "100"
            })
          })
        })]
      })
    });
  };
  const renderDraggableDependentFormFieldInput = () => {
    const isSelected = dependentFieldFilter.dependentFormField.name === selectedFieldName;
    const isHidden = dependentFieldFilter.dependentFormField.hidden;
    return /*#__PURE__*/_jsx(DraggableFieldContainer, {
      onClick: handleSelectedChange,
      children: /*#__PURE__*/_jsx(DraggableField, {
        $isHovered: isHovered,
        $isHidden: isHidden,
        $isSelected: isSelected,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onFocus: handleMouseEnter,
        onBlur: handleMouseLeave,
        "data-test-id": "dependent-field-draggable-field",
        children: /*#__PURE__*/_jsx(FormFieldInput, {
          formField: dependentFieldFilter.dependentFormField,
          isFormLabelPropertyEnabled: isFormLabelPropertyEnabled,
          formFieldError: formFieldError
        })
      })
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderDependentFieldFilterRule(), renderDraggableDependentFormFieldInput()]
  });
};
export default DependentFormFieldInput;